import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, AlertTriangle, CalendarClock, CheckCircle2,
  ChevronRight, ChevronDown, Calendar, Users, RefreshCw, History,
} from 'lucide-react';
import api from '../../api/auth.api';

// ============================================================================
// Bidding (list) — PWA admin view of active Bid Room projects. Card-per-
// project, urgency-pinned, filterable. Tap a card -> /bidding/:id.
// Read-only — sending invites stays on the desktop admin.
// ============================================================================

// Local types — mirror the BFA response shape so we don't import bfav5 types.
interface BidProjectSummary {
  bid_project_id: number;
  project_name: string;
  external_project_number: string | null;
  location: string | null;
  sub_bid_due_date: string | null;
  invite_count: number;
  // will_bid_count includes bid_submitted subs; submitted_count is the
  // subset whose bid is already in.
  will_bid_count: number;
  submitted_count: number;
  declining_count: number;
  gc_count: number;
  latest_gc_due: string | null;
  critical_trade_count: number;
  trades_total: number;
  in_critical_window: boolean;
}

type Filter = 'all' | 'critical' | 'this_week';

const colors = {
  bg: '#f8fafc', cardBg: '#fff',
  text: '#1e293b', textMuted: '#64748b', textFaint: '#94a3b8',
  border: '#e2e8f0',
  primary: '#3b82f6',
  critical: '#dc2626', criticalBg: '#fee2e2',
  warn: '#a16207', warnBg: '#fef3c7',
  ok: '#059669', okBg: '#dcfce7',
};

export const Bidding: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<BidProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [showPast, setShowPast] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/api/planroom/pwa/projects');
      setItems(r.data?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load bid projects');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Log a "bid-room" page visit so the super-admin activity view counts PWA
  // opens too — same DASHBOARD_VISITS key as bfav5's /bid-room, so the two
  // producers aggregate. Tagged source=pwa. Fire-and-forget (analytics only).
  useEffect(() => {
    api.post('/api/admin/dashboard/visit', { page: 'bid-room', source: 'pwa' }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfToday); endOfWeek.setDate(endOfWeek.getDate() + 7);
    let xs = items;
    if (filter === 'critical') xs = xs.filter(p => p.critical_trade_count > 0);
    else if (filter === 'this_week') xs = xs.filter(p => {
      if (!p.sub_bid_due_date) return false;
      const d = new Date(p.sub_bid_due_date);
      return d >= startOfToday && d <= endOfWeek;
    });
    // Critical first, then by due date ascending (null due dates last).
    const sorted = [...xs].sort((a, b) => {
      const aCrit = a.critical_trade_count > 0 ? 1 : 0;
      const bCrit = b.critical_trade_count > 0 ? 1 : 0;
      if (aCrit !== bCrit) return bCrit - aCrit;
      const ad = a.sub_bid_due_date ? new Date(a.sub_bid_due_date).getTime() : Infinity;
      const bd = b.sub_bid_due_date ? new Date(b.sub_bid_due_date).getTime() : Infinity;
      return ad - bd;
    });
    // Bids whose due date has passed collapse under "Past bids" (they hang
    // around up to 7 days until auto-archive sweeps them) — newest first.
    const isPast = (p: BidProjectSummary) => {
      if (!p.sub_bid_due_date) return false;
      const d = daysUntil(p.sub_bid_due_date);
      return d !== null && d < 0;
    };
    return {
      current: sorted.filter(p => !isPast(p)),
      past: sorted.filter(isPast).sort((a, b) =>
        new Date(b.sub_bid_due_date!).getTime() - new Date(a.sub_bid_due_date!).getTime()),
    };
  }, [items, filter]);

  const totals = useMemo(() => ({
    total: items.length,
    critical: items.reduce((s, p) => s + p.critical_trade_count, 0),
    thisWeek: items.filter(p => {
      if (!p.sub_bid_due_date) return false;
      const days = daysUntil(p.sub_bid_due_date);
      return days !== null && days >= 0 && days <= 7;
    }).length,
  }), [items]);

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      {/* Title bar (no back button — PWA's global Header handles that) */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: colors.cardBg, borderBottom: `1px solid ${colors.border}`,
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <h1 style={{ flex: 1, margin: 0, fontSize: 22, fontWeight: 800, color: colors.text, letterSpacing: -0.3 }}>
          Bidding
        </h1>
        <button onClick={load} disabled={loading} style={iconBtn} aria-label="Reload" title="Reload">
          <RefreshCw size={20} color={loading ? colors.textFaint : colors.text} />
        </button>
      </div>

      {/* Stat strip */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px 0' }}>
        <Stat icon={<FileText size={14} />}      label="Active"   value={totals.total}    accent={colors.primary} />
        <Stat icon={<CalendarClock size={14} />} label="This week" value={totals.thisWeek} accent={colors.warn} />
        <Stat icon={<AlertTriangle size={14} />} label="Critical" value={totals.critical} accent={colors.critical} />
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 4px', overflowX: 'auto' }}>
        {([
          ['all', 'All'],
          ['critical', 'Critical'],
          ['this_week', 'Due this week'],
        ] as [Filter, string][]).map(([f, label]) => (
          <button key={f} onClick={() => setFilter(f)} style={pill(filter === f)}>
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 16px 32px' }}>
        {error && <ErrorBanner msg={error} onRetry={load} />}

        {loading ? (
          <p style={{ color: colors.textMuted, fontSize: 14 }}>Loading…</p>
        ) : filtered.current.length === 0 && filtered.past.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.current.map(p => <ProjectCard key={p.bid_project_id} project={p} onOpen={() => navigate(`/bidding/${p.bid_project_id}`)} />)}
            {filtered.current.length === 0 && (
              <p style={{ color: colors.textMuted, fontSize: 14, margin: 0 }}>No current bids.</p>
            )}

            {/* Past bids — collapsed by default so the list stays current */}
            {filtered.past.length > 0 && (
              <>
                <button
                  onClick={() => setShowPast(s => !s)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '12px 14px', marginTop: 6,
                    background: colors.cardBg, border: `1px solid ${colors.border}`,
                    borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%',
                  }}
                >
                  {showPast ? <ChevronDown size={16} color={colors.textFaint} /> : <ChevronRight size={16} color={colors.textFaint} />}
                  <History size={15} color={colors.textFaint} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: colors.textMuted }}>
                    Past bids
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: colors.textFaint }}>
                    {filtered.past.length}
                  </span>
                </button>
                {showPast && filtered.past.map(p => (
                  <ProjectCard key={p.bid_project_id} project={p} onOpen={() => navigate(`/bidding/${p.bid_project_id}`)} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ----- helpers / sub-components -----------------------------------------------

const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

function pill(active: boolean): React.CSSProperties {
  return {
    flexShrink: 0,
    padding: '8px 16px', borderRadius: 18, fontSize: 14, fontWeight: 600,
    border: `1px solid ${active ? colors.primary : colors.border}`,
    background: active ? colors.primary : colors.cardBg,
    color: active ? '#fff' : colors.text,
    cursor: 'pointer',
  };
}

function daysUntil(iso: string): number | null {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return null;
  const start = new Date(); start.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - start.getTime()) / 86400000);
}

function dueLabel(d: string | null): { text: string; color: string; bg: string } | null {
  if (!d) return null;
  const days = daysUntil(d);
  if (days === null) return null;
  if (days < 0)   return { text: `${Math.abs(days)}d OVERDUE`, color: '#991b1b', bg: '#fee2e2' };
  if (days === 0) return { text: 'DUE TODAY',                  color: '#9a3412', bg: '#fef3c7' };
  if (days === 1) return { text: 'due tomorrow',               color: '#9a3412', bg: '#fef3c7' };
  if (days <= 7)  return { text: `due in ${days}d`,            color: '#a16207', bg: '#fef3c7' };
  return null;
}

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number; accent: string }> = ({ icon, label, value, accent }) => (
  <div style={{
    flex: 1, background: colors.cardBg, border: `1px solid ${colors.border}`,
    borderRadius: 10, padding: '12px 14px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: accent, marginBottom: 6 }}>
      {icon}
      <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: colors.text, lineHeight: 1, letterSpacing: -0.4 }}>{value}</div>
  </div>
);

const ProjectCard: React.FC<{ project: BidProjectSummary; onOpen: () => void }> = ({ project: p, onOpen }) => {
  const due = dueLabel(p.sub_bid_due_date);
  const isCritical = p.critical_trade_count > 0;
  return (
    <button onClick={onOpen} style={{
      textAlign: 'left', width: '100%', padding: 0,
      background: colors.cardBg, border: `1px solid ${isCritical ? '#fecaca' : colors.border}`,
      borderRadius: 10, cursor: 'pointer', overflow: 'hidden',
    }}>
      {isCritical && (
        <div style={{
          background: colors.criticalBg, color: colors.critical,
          padding: '8px 16px', fontSize: 12, fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: 0.6,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertTriangle size={13} />
          {p.critical_trade_count} trade{p.critical_trade_count === 1 ? '' : 's'} waiting on commits
        </div>
      )}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, letterSpacing: -0.2 }}>{p.project_name}</div>
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {p.location && <span>{p.location}</span>}
              {p.external_project_number && <span>· #{p.external_project_number}</span>}
            </div>
            <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {p.sub_bid_due_date && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Calendar size={13} /> {new Date(p.sub_bid_due_date).toLocaleDateString()}
                </span>
              )}
              {due && (
                <span style={{ ...pillStyle, background: due.bg, color: due.color }}>{due.text}</span>
              )}
            </div>
          </div>
          <ChevronRight size={20} color={colors.textFaint} />
        </div>

        <div style={{
          marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.border}`,
          display: 'flex', gap: 16, fontSize: 14, color: colors.textMuted, flexWrap: 'wrap',
        }}>
          <span><strong style={{ color: colors.text, fontSize: 15 }}>{p.invite_count}</strong> invited</span>
          {(p.submitted_count || 0) > 0 && <span style={{ color: '#1d4ed8' }}><strong style={{ fontSize: 15 }}>{p.submitted_count}</strong> submitted</span>}
          {p.will_bid_count - (p.submitted_count || 0) > 0 && <span style={{ color: colors.ok }}><strong style={{ fontSize: 15 }}>{p.will_bid_count - (p.submitted_count || 0)}</strong> will bid</span>}
          {p.declining_count > 0 && <span style={{ color: colors.critical }}><strong style={{ fontSize: 15 }}>{p.declining_count}</strong> declined</span>}
          {p.gc_count > 0 && <span><Users size={13} style={{ verticalAlign: 'middle' }} /> {p.gc_count} GC{p.gc_count === 1 ? '' : 's'}</span>}
        </div>
      </div>
    </button>
  );
};

const pillStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 800,
  textTransform: 'uppercase', letterSpacing: 0.5,
};

const EmptyState: React.FC<{ filter: Filter }> = ({ filter }) => (
  <div style={{
    background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: 10,
    padding: '48px 20px', textAlign: 'center',
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: '50%', background: colors.okBg, color: colors.ok,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
    }}>
      <CheckCircle2 size={22} />
    </div>
    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>
      {filter === 'critical' ? 'No critical tasks' :
       filter === 'this_week' ? 'No bids due this week' :
       'No active bid projects'}
    </h3>
    <p style={{ margin: '8px 0 0', fontSize: 15, color: colors.textMuted }}>
      You're caught up — pull to refresh or check back later.
    </p>
  </div>
);

const ErrorBanner: React.FC<{ msg: string; onRetry: () => void }> = ({ msg, onRetry }) => (
  <div style={{
    background: colors.criticalBg, border: `1px solid #fecaca`, borderRadius: 8,
    padding: '10px 12px', marginBottom: 12,
    display: 'flex', alignItems: 'center', gap: 8, color: '#991b1b', fontSize: 13,
  }}>
    <AlertTriangle size={14} /> <span style={{ flex: 1 }}>{msg}</span>
    <button onClick={onRetry} style={{ background: 'transparent', border: '1px solid #fca5a5', borderRadius: 4, padding: '4px 10px', color: '#991b1b', fontSize: 12, cursor: 'pointer' }}>Retry</button>
  </div>
);

export default Bidding;
